import { useCallback, useMemo } from 'react';

import {
  type AssetsSegmentSchema,
  httpGetAssetsByAssetIdSegments,
} from '@iconik-io/iconik-api/src/services/assets';

import type { CommonListGetParams } from '../../../../redux/common_list/thunks';
import type { AppDispatch } from '../../../../redux/types';
import { getPlaylistItemVersionIds } from '../../../../utils/get_playlist_item_version_ids';
import type { SortOrder } from '../../../../utils/sort/types';
import type { PlaylistItemVersionParam } from '../../../../utils/types/playlist';
import { useEntityList } from '../../../hooks/entity/use_entity_list';
import {
  type CommentListFilters,
  selectCommentRepliesMap,
  selectFilteredRootComments,
  selectRootIdsWithMatchingReply,
  selectTotalRepliesCountMap,
} from './selectors';

export type CommentSortExpression = `${keyof AssetsSegmentSchema},${SortOrder}`;

export function useCommentEntityList(
  assetId: string,
  filters: {
    annotationFilter?: string[];
    completionFilter?: string[];
    replyFilter?: string[];
    mentionFilter?: string[];
    commenterFilter?: string[];
    colorFilter?: string[];
    textFilter?: string;
    versionFilter?: string;
    sort?: CommentSortExpression;
    visibilityFilter?: string[];
  },
  shareId?: string,
  shareType?: string,
  showExistingComments?: boolean,
  isInternalUser?: boolean,
  originalPlaylistItemVersionId?: PlaylistItemVersionParam,
) {
  const getList = useCallback(
    (dispatch: AppDispatch, params: CommonListGetParams) => {
      return dispatch(
        httpGetAssetsByAssetIdSegments(assetId, {
          segment_type: 'COMMENT',
          per_page: 100,
          include_all_versions: true,
          page: params.page,
          include_users: true,
        }),
      );
    },
    [assetId],
  );
  const {
    annotationFilter,
    completionFilter,
    replyFilter,
    mentionFilter,
    commenterFilter,
    colorFilter,
    textFilter,
    versionFilter,
    sort,
    visibilityFilter,
  } = filters;

  const result = useEntityList({
    entityType: 'segments',
    listName: 'panelComments',
    getList,
    fetchAllPages: true,
  });

  const selectedVersions = getPlaylistItemVersionIds(
    originalPlaylistItemVersionId,
  );

  // TODO: Move 'filteredResultEntities' logic to the backend for better maintainability and consistency.
  // https://linear.app/iconik/issue/RP-2767/add-ability-to-filter-by-share-to-httpgetassetsbyassetidsegments-and
  const filteredResultEntities = useMemo(() => {
    return result.entities.filter((comment) => {
      // TODO: Refine this logic when allowing internal comments to be shown to properly authenticated users
      // see: https://linear.app/iconik/issue/RP-2189/allow-logged-in-users-in-shares-to-create-and-view-internal-comments
      if (comment.is_internal && !isInternalUser) {
        return false;
      }

      if (
        originalPlaylistItemVersionId &&
        selectedVersions &&
        comment.version_id
      ) {
        return selectedVersions.includes(comment.version_id);
      }

      // Playlist shares should only show comments from the specific share
      // TODO: add segment filtering based on share id and type under RP-2432
      if (
        shareId &&
        shareType === 'playlists' &&
        !showExistingComments &&
        comment.share_id !== shareId
      ) {
        return false;
      }

      return true;
    });
  }, [
    result.entities,
    shareId,
    shareType,
    showExistingComments,
    isInternalUser,
    selectedVersions,
    originalPlaylistItemVersionId,
  ]);

  const selectorFilters = useMemo<CommentListFilters>(
    () => ({
      annotationFilter,
      completionFilter,
      replyFilter,
      mentionFilter,
      commenterFilter,
      colorFilter,
      textFilter,
      versionFilter,
      sort,
      visibilityFilter,
    }),
    [
      annotationFilter,
      completionFilter,
      replyFilter,
      mentionFilter,
      commenterFilter,
      colorFilter,
      textFilter,
      versionFilter,
      sort,
      visibilityFilter,
    ],
  );

  const entities = selectFilteredRootComments(
    filteredResultEntities,
    selectorFilters,
  );
  const repliesMap = selectCommentRepliesMap(
    filteredResultEntities,
    selectorFilters,
  );
  const rootIdsWithMatchingReply = selectRootIdsWithMatchingReply(
    filteredResultEntities,
    selectorFilters,
  );
  const totalRepliesCountMap = selectTotalRepliesCountMap(
    filteredResultEntities,
    selectorFilters,
  );

  return {
    ...result,
    entities,
    repliesMap,
    rootIdsWithMatchingReply,
    totalRepliesCountMap,
    baseEntities: filteredResultEntities,
  };
}
